import { ATeacher } from 'kuaidong-api'
import { Route, NavigationGuardNext } from 'vue-router'
import { LoginMethod, GlobalConfigRouteMetaEnum } from 'kuaidong-model'
import { AppUtil, BrowserUtil, RouterUtil, EventEmitter, VueUtil, BaseLogger } from 'kuaidong-lib'
import { loginStore, teacherStore, globalConfigStore, competitionStore, menuRoleStore } from 'kuaidong-store'

import router, {
  ignoreAuthRoutes,
  ignoreLoginRoutes,
  ignoreLoginInBrowserRoutes,
  // routers
  authRouter,
  customRouter,
  organizationRouter,
  workContestRouter,
} from '../router'

export enum LoginEvent {
  Authed = 'authed',
  Logined = 'logined',
}

const eventEmitter = new EventEmitter()

const WXWebOauthTargetHash = 'wx-web-oauth-target-hash'

const OrganizationTag = organizationRouter.default[0].path

const ContestTag = workContestRouter.default[0].path

export default class LoginService {
  public static once(type: string, handler: Function) {
    eventEmitter.once(type, handler)
  }

  public static async initBeforeLogin(route?: Route) {
    if (route && route.meta) {
      if (route.meta[GlobalConfigRouteMetaEnum.All]) {
        await globalConfigStore.ensureConfigIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.Drills]) {
        await globalConfigStore.ensureDrillsIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.Homework]) {
        await globalConfigStore.ensureHomeworkIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.DrillCategories]) {
        await globalConfigStore.ensureDrillCategoriesIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.CompetitionType]) {
        await competitionStore.ensureTypesIsLoaded()
      }
    }
  }

  public static async initAfterAuthed() {
    if (loginStore.isLoginedByOrganization) {
      return
    }

    if (!teacherStore.teacher) {
      await teacherStore.loadTeacher()
    }

    if (!teacherStore.userRoleInfo) {
      await teacherStore.loadUserRoleInfo()
    }

    if (!menuRoleStore.menuRole) {
      await menuRoleStore.ensureMenuRoleConfigIsLoaded()
    }
  }

  public static async route(target: string) {
    location.href = target
  }

  public static async routeToAuth(_route: Route) {
    const route = (await this.isApplied()) ? authRouter.authInProgressRoute : authRouter.authRoute

    if (_route.name !== route.name) {
      router.replace({ name: route.name, query: { channel: _route.query?.qd } })
    }
  }

  public static async isApplied() {
    const authRecords = await ATeacher.allAuthRecords()
    return authRecords && authRecords.length > 0
  }

  public static isOrganizationPath(url: string) {
    return !!(url && url.includes(OrganizationTag))
  }

  public static isCustomGuangdongPath(url: string) {
    return !!(url && url.includes(customRouter.customGuangdongRouter.dashboardRoute.path))
  }

  public static isCustomCompetitionPath(url: string) {
    return !!(url && url.includes(customRouter.customCompetitionRouter.dashboardRoute.path))
  }

  public static hasWXWebOauthTargetHash(href: string) {
    return href && href.includes(WXWebOauthTargetHash)
  }

  public static encodeWXWebOauthTargetHash(href: string) {
    const url = new URL(href)
    if (url.hash) {
      return `${url.origin}${url.pathname}?${WXWebOauthTargetHash}=${url.hash.substring(2)}`
    } else {
      return href
    }
  }

  public static decodeWXWebOauthTargetHash(href: string) {
    if (this.hasWXWebOauthTargetHash(href)) {
      let hash = ''
      const url = new URL(href)

      if (url.searchParams.has(WXWebOauthTargetHash)) {
        hash = url.searchParams.get(WXWebOauthTargetHash) || ''
      }

      return `${url.origin}${url.pathname}#/${hash}`
    }

    return href
  }

  public static checkWXWebOauthTargetAndRedirect(href = location.href) {
    if (href && this.hasWXWebOauthTargetHash(href) && !RouterUtil.isCurRoute(authRouter.loginRoute, router)) {
      location.replace(this.decodeWXWebOauthTargetHash(href))

      return true
    }

    return false
  }

  public static async login(_route?: Route) {
    if (!(await AppUtil.isUnderApp())) {
      // 当前页已经是登录页不做跳转
      if (
        !_route &&
        (RouterUtil.isCurRoute(authRouter.loginRoute, router) ||
          RouterUtil.isCurRoute(customRouter.customGuangdongRouter.loginRoute, router) ||
          RouterUtil.isCurRoute(customRouter.customCompetitionRouter.loginRoute, router))
      ) {
        return false
      }

      let queryStr: string

      if (_route && _route.fullPath) {
        queryStr = `${location.origin}${location.pathname}#${_route.fullPath}`
      } else {
        queryStr = location.href
      }

      if (BrowserUtil.isWechat() && !BrowserUtil.isWxWork()) {
        // 微信重定向到target时会将hash部分抹去，所以需要处理下target。
        // https://www.cnblogs.com/fan-zha/p/11250549.html
        queryStr = this.encodeWXWebOauthTargetHash(queryStr)

        // 微信的登录地址, 微信登录之后固定跳转到当前页面
        location.href = `${process.env.VUE_APP_CRAZYROPE_API}/wx_web_oauth_entry?target=${queryStr}`
      } else {
        // 普通网页的登录地址
        let routeName = authRouter.loginRoute.name

        const query = {
          target: queryStr,
          loginMethod: LoginMethod.ByPhone,
        }

        if (LoginService.isCustomGuangdongPath(queryStr)) {
          routeName = customRouter.customGuangdongRouter.loginRoute.name
          query.loginMethod = LoginMethod.ByAccount
        } else if (LoginService.isCustomCompetitionPath(queryStr)) {
          routeName = customRouter.customCompetitionRouter.loginRoute.name
          query.loginMethod = LoginMethod.ByAccount
        }

        router.replace({ name: routeName, query })
      }

      return true
    }

    return false
  }

  public static async needAuth(_route: Route) {
    if (_route.name && (_route.name.startsWith('auth') || _route.name.startsWith('ad_auth'))) {
      return false
    }

    if (ignoreAuthRoutes.some((route) => _route.name === route.name)) {
      return false
    }

    if (_route.meta?.ignoreAuth) {
      return false
    }

    return true
  }

  public static async needLogin(_route: Route) {
    if (_route.name === authRouter.loginRoute.name) {
      return false
    }

    if (ignoreLoginRoutes.some((route) => _route.name === route.name)) {
      return false
    }

    if (_route.meta?.ignoreLogin) {
      return false
    }

    if (!BrowserUtil.isWechat() && !(await AppUtil.isUnderApp())) {
      if (ignoreLoginInBrowserRoutes.some((route) => _route.name === route.name)) {
        return false
      }
    }

    return true
  }

  public static async routerBeforeEachHook(_to: Route, _from: Route, _next: NavigationGuardNext<Vue>) {
    if (!(await LoginService.needLogin(_to))) {
      return _next()
    }

    // Check if it is logined/authed already.
    if (loginStore.userTeacherId === null) {
      if (_to.path.includes(OrganizationTag) || _to.path.includes(ContestTag)) {
        await loginStore.loadOrganization()

        if (loginStore.isLoginedByOrganization) {
          BaseLogger.setUserId(loginStore.organization!.id)
          VueUtil.setARMSConfig({ uid: loginStore.organization!.id })
        }
      } else {
        await loginStore.loadUserTeacherId()

        if (loginStore.isLogined) {
          BaseLogger.setUserId(loginStore.userTeacherId!.user_id!)
          VueUtil.setARMSConfig({ uid: loginStore.userTeacherId!.user_id })
        }
      }
    }

    LoginService.initBeforeLogin(_to)

    if (!loginStore.isLogined) {
      _next(false)

      return LoginService.login(_to)
    }

    eventEmitter.fire(LoginEvent.Logined)

    if (!(await LoginService.needAuth(_to))) {
      return _next()
    }

    if (loginStore.isAuthed) {
      await LoginService.initAfterAuthed()

      return _next()
    } else {
      _next(false)

      return LoginService.routeToAuth(_to)
    }
  }
}
